import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Slider,
  FormControlLabel,
  Switch,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Psychology as AIIcon,
  ExpandMore as ExpandMoreIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import complianceService, { SanctionCheckResult } from '../../services/complianceService';

const SanctionsScreener: React.FC = () => {
  const [entityName, setEntityName] = useState('');
  const [entityType, setEntityType] = useState('company');
  const [fuzzyMatching, setFuzzyMatching] = useState(true);
  const [threshold, setThreshold] = useState(85);
  const [batchEntities, setBatchEntities] = useState<Array<{ name: string; type: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SanctionCheckResult[]>([]);
  const [mode, setMode] = useState<'single' | 'batch'>('single');
  const [listsChecked, setListsChecked] = useState<string[]>([]);

  // Match display control - tracks how many matches to show per entity
  const [displayedMatchCounts, setDisplayedMatchCounts] = useState<{ [key: number]: number }>({});

  // AI Enhancement States
  const [useAIEnhancement, setUseAIEnhancement] = useState(false);
  const [useSemanticMatching, setUseSemanticMatching] = useState(false);
  const [useAliasGeneration, setUseAliasGeneration] = useState(false);
  const [enhancedResults, setEnhancedResults] = useState<any>(null);
  const [semanticMatches, setSemanticMatches] = useState<any[]>([]);

  const handleSingleCheck = async () => {
    if (!entityName.trim()) {
      toast.error('Please enter an entity name');
      return;
    }

    setIsLoading(true);
    try {
      // Run standard sanctions check
      const result = await complianceService.checkSanctions(
        entityName,
        entityType,
        fuzzyMatching,
        threshold / 100
      );
      setResults([result]);
      
      // Run AI-enhanced check if enabled
      if (useAIEnhancement) {
        const enhancedResult = await complianceService.enhanceSanctionsCheck(
          entityName,
          entityType,
          useAliasGeneration
        );
        setEnhancedResults(enhancedResult);
        
        // If semantic matching is enabled and we have potential matches
        if (useSemanticMatching && enhancedResult.potential_matches) {
          const semanticResults = [];
          for (const match of enhancedResult.potential_matches.slice(0, 5)) {
            const similarity = await complianceService.checkSemanticSimilarity(
              entityName,
              match.name
            );
            semanticResults.push({
              ...match,
              semantic: similarity
            });
          }
          setSemanticMatches(semanticResults);
        }
      }
      
      // Check for authentication error
      if ((result as any).auth_required || (result as any).error_category === 'authentication') {
        toast.error(
          <div>
            <div>{(result as any).error || 'Authentication required'}</div>
            <button
              onClick={() => window.location.href = '/profile'}
              style={{
                marginTop: '8px',
                padding: '4px 12px',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go to Profile
            </button>
          </div>,
          { autoClose: 10000 }
        );
        return;
      }

      // Check for other errors
      if ((result as any).error) {
        toast.error((result as any).error);
        return;
      }

      setResults([result]);

      // Update lists checked from API response
      if (result.lists_checked && Array.isArray(result.lists_checked)) {
        setListsChecked(result.lists_checked);
      }

      // Show success/warning based on result
      if (result.is_sanctioned) {
        toast.error(`⚠️ Entity is sanctioned! Found on ${result.matched_lists?.length || 0} list(s)`);
      } else if (enhancedResults?.high_risk_indicators?.length > 0) {
        toast.warning('⚠️ Entity cleared but has risk indicators');
      } else {
        toast.success('✅ Entity is not on any sanctions list');
      }
    } catch (error: any) {
      // Handle different error types
      const errorMessage = error?.response?.data?.detail?.message || error?.message || 'Failed to check sanctions';

      if (errorMessage.includes('authentication') || errorMessage.includes('log in')) {
        toast.error(
          <div>
            <div>{errorMessage}</div>
            <button
              onClick={() => window.location.href = '/profile'}
              style={{
                marginTop: '8px',
                padding: '4px 12px',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Go to Profile
            </button>
          </div>,
          { autoClose: 10000 }
        );
      } else {
        toast.error(errorMessage);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchCheck = async () => {
    if (batchEntities.length === 0) {
      toast.error('Please add entities to check');
      return;
    }

    setIsLoading(true);
    try {
      const results = await complianceService.checkSanctionsBatch(batchEntities);
      setResults(results);

      // Update lists checked from first result's response
      if (results.length > 0 && results[0].lists_checked && Array.isArray(results[0].lists_checked)) {
        setListsChecked(results[0].lists_checked);
      }

      const sanctionedCount = results.filter((r: any) => r.is_sanctioned).length;
      if (sanctionedCount > 0) {
        toast.error(`⚠️ ${sanctionedCount} entities are sanctioned!`);
      } else {
        toast.success(`✅ All ${results.length} entities are clear`);
      }
    } catch (error) {
      toast.error('Failed to check sanctions');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBatchEntity = () => {
    if (entityName.trim()) {
      setBatchEntities([...batchEntities, { name: entityName, type: entityType }]);
      setEntityName('');
    }
  };

  const handleRemoveBatchEntity = (index: number) => {
    setBatchEntities(batchEntities.filter((_, i) => i !== index));
  };

  const getMatchColor = (score: number) => {
    if (score >= 90) return 'error';
    if (score >= 70) return 'warning';
    if (score >= 50) return 'info';
    return 'default';
  };

  const getSanctionLists = () => {
    // If we have dynamic lists from API response, use them
    if (listsChecked.length > 0) {
      return listsChecked.map(listCode => {
        // Parse list code to generate readable name
        const parts = listCode.split('_');
        const jurisdiction = parts[0] || listCode;
        const name = listCode
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        return {
          code: listCode,
          name: name,
          jurisdiction: jurisdiction.toUpperCase()
        };
      });
    }

    // Fallback to default lists if no API data yet
    return [
      { code: 'OFAC_SDN', name: 'OFAC SDN List', jurisdiction: 'US' },
      { code: 'UN_CONSOLIDATED', name: 'UN Consolidated List', jurisdiction: 'UN' },
      { code: 'EU_CONSOLIDATED', name: 'EU Consolidated List', jurisdiction: 'EU' },
      { code: 'UK_CONSOLIDATED', name: 'UK Sanctions List', jurisdiction: 'UK' },
      { code: 'CANADA_SEMA', name: 'Canada SEMA', jurisdiction: 'Canada' },
      { code: 'AUSTRALIA_DFAT', name: 'Australia DFAT', jurisdiction: 'Australia' },
      { code: 'JAPAN_METI', name: 'Japan METI', jurisdiction: 'Japan' },
      { code: 'SWITZERLAND_SECO', name: 'Swiss SECO', jurisdiction: 'Switzerland' },
      { code: 'SINGAPORE_MAS', name: 'Singapore MAS', jurisdiction: 'Singapore' },
    ];
  };

  return (
    <Box>
      {/* Input Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant={mode === 'single' ? 'contained' : 'outlined'}
                onClick={() => setMode('single')}
              >
                Single Entity
              </Button>
              <Button
                variant={mode === 'batch' ? 'contained' : 'outlined'}
                onClick={() => setMode('batch')}
              >
                Batch Screening
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Entity Name"
              value={entityName}
              onChange={(e) => setEntityName(e.target.value)}
              placeholder="Enter company, individual, or vessel name"
              disabled={isLoading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Entity Type"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              SelectProps={{ native: true }}
              disabled={isLoading}
            >
              <option value="company">Company</option>
              <option value="individual">Individual</option>
              <option value="vessel">Vessel</option>
              <option value="aircraft">Aircraft</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            {mode === 'single' ? (
              <Button
                fullWidth
                variant="contained"
                onClick={handleSingleCheck}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ height: '56px' }}
              >
                {isLoading ? 'Checking...' : 'Check Sanctions'}
              </Button>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAddBatchEntity}
                disabled={isLoading}
                startIcon={<AddIcon />}
                sx={{ height: '56px' }}
              >
                Add to Batch
              </Button>
            )}
          </Grid>

          {/* Advanced Options */}
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Advanced Options
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Standard Options */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Standard Matching Options
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={fuzzyMatching}
                              onChange={(e) => setFuzzyMatching(e.target.checked)}
                            />
                          }
                          label="Fuzzy Matching"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography gutterBottom>
                          Match Threshold: {threshold}%
                        </Typography>
                        <Slider
                          value={threshold}
                          onChange={(e, value) => setThreshold(value as number)}
                          disabled={!fuzzyMatching || isLoading}
                          min={50}
                          max={100}
                          marks={[
                            { value: 50, label: '50%' },
                            { value: 75, label: '75%' },
                            { value: 100, label: '100%' },
                          ]}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  {/* AI Enhancement Options */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      AI Enhancement Options (Powered by Mistral)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={useAIEnhancement}
                              onChange={(e) => {
                                setUseAIEnhancement(e.target.checked);
                                if (!e.target.checked) {
                                  setUseSemanticMatching(false);
                                  setUseAliasGeneration(false);
                                }
                              }}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AIIcon fontSize="small" />
                              <span>Enable AI Enhancement</span>
                            </Box>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={useSemanticMatching}
                              onChange={(e) => setUseSemanticMatching(e.target.checked)}
                              disabled={!useAIEnhancement}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CompareIcon fontSize="small" />
                              <span>Semantic Matching</span>
                            </Box>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={useAliasGeneration}
                              onChange={(e) => setUseAliasGeneration(e.target.checked)}
                              disabled={!useAIEnhancement}
                            />
                          }
                          label="Generate Aliases"
                        />
                      </Grid>
                    </Grid>
                    {useAIEnhancement && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          AI enhancement uses advanced language models to:
                          {useSemanticMatching && ' • Match entities based on meaning, not just text similarity'}
                          {useAliasGeneration && ' • Generate potential aliases and variations of entity names'}
                          {' • Identify high-risk patterns and indicators'}
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {/* Batch Entities List */}
        {mode === 'batch' && batchEntities.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Entities to Screen ({batchEntities.length})
            </Typography>
            <Paper sx={{ p: 1 }}>
              {batchEntities.map((entity, index) => (
                <Chip
                  key={index}
                  label={`${entity.name} (${entity.type})`}
                  onDelete={() => handleRemoveBatchEntity(index)}
                  sx={{ m: 0.5 }}
                />
              ))}
            </Paper>
            <Button
              variant="contained"
              onClick={handleBatchCheck}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ mt: 2 }}
            >
              {isLoading ? 'Checking Batch...' : `Check ${batchEntities.length} Entities`}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Lists Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sanctions Lists Checked
        </Typography>
        <Grid container spacing={2}>
          {getSanctionLists().map((list) => (
            <Grid item xs={12} sm={6} md={4} key={list.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  <strong>{list.jurisdiction}:</strong> {list.name}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Alert severity="info" sx={{ mt: 2 }}>
          {listsChecked.length > 0
            ? `Screening performed against ${listsChecked.length} sanctions lists via BEO Sanctions API (updated daily)`
            : 'All checks are performed against the latest sanctions lists updated daily'}
        </Alert>
      </Paper>

      {/* Results Section */}
      {results.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Screening Results
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entity</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Match Score</TableCell>
                  <TableCell>Matched Lists</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {result.entity_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{result.entity_type}</TableCell>
                    <TableCell>
                      {result.is_sanctioned ? (
                        <Chip
                          icon={<ErrorIcon />}
                          label="SANCTIONED"
                          color="error"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<CheckIcon />}
                          label="CLEAR"
                          color="success"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${result.match_score.toFixed(0)}%`}
                        color={getMatchColor(result.match_score)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.matched_lists && result.matched_lists.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {result.matched_lists.map((list) => (
                            <Chip
                              key={list}
                              label={list}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No matches
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* BEO Lists Checked Section */}
          {listsChecked.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info">
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  📋 BEO Sanctions Lists Scanned ({listsChecked.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {listsChecked.map(list => (
                    <Chip
                      key={list}
                      label={list}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  Global coverage: EU, UN, OFAC SDN, Australia, Canada, Japan, India and more
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Detailed Matches Section */}
          {results.some(r => r.is_sanctioned && r.matches && r.matches.length > 0) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom color="error">
                ⚠️ Sanctions Match Details
              </Typography>
              {results.filter(r => r.is_sanctioned).map((result, resIdx) => (
                <Box key={resIdx} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    {result.entity_name}
                    <Chip
                      label={`${result.match_count || result.matches?.length || 0} matches`}
                      color="error"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </Typography>

                  {result.matched_lists && result.matched_lists.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Matched Lists:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {result.matched_lists.map(list => (
                          <Chip
                            key={list}
                            label={list}
                            size="small"
                            color="error"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Individual Match Accordions */}
                  {result.matches && result.matches.slice(0, displayedMatchCounts[resIdx] || 10).map((match: any, matchIdx: number) => (
                    <Accordion key={matchIdx} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {match.name || match.full_name}
                          </Typography>
                          {match.source_list && (
                            <Chip
                              label={match.source_list}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {match.regime && (
                            <Chip
                              label={match.regime}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {match.group_id && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Group ID:</Typography>
                              <Typography variant="body2">{match.group_id}</Typography>
                            </Grid>
                          )}
                          {match.group_type && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Type:</Typography>
                              <Typography variant="body2">{match.group_type}</Typography>
                            </Grid>
                          )}
                          {match.listed_on && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Listed On:</Typography>
                              <Typography variant="body2">{match.listed_on}</Typography>
                            </Grid>
                          )}
                          {match.dob && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Date of Birth:</Typography>
                              <Typography variant="body2">{match.dob}</Typography>
                            </Grid>
                          )}
                          {match.country && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Country:</Typography>
                              <Typography variant="body2">{match.country}</Typography>
                            </Grid>
                          )}
                          {match.address && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Address:</Typography>
                              <Typography variant="body2">{match.address}</Typography>
                            </Grid>
                          )}
                          {match.other_info && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Additional Information:</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>{match.other_info}</Typography>
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}

                  {/* Show More Button and Status */}
                  {result.matches && result.matches.length > (displayedMatchCounts[resIdx] || 10) && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Alert severity="info" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2">
                          Showing {displayedMatchCounts[resIdx] || 10} of {result.matches.length} matches
                          {result.match_count && result.match_count > result.matches.length && (
                            <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                              ({result.match_count} total found, displaying first {result.matches.length})
                            </span>
                          )}
                        </Typography>
                      </Alert>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          const currentCount = displayedMatchCounts[resIdx] || 10;
                          const nextCount = Math.min(
                            currentCount === 10 ? 20 :
                            currentCount === 20 ? 50 :
                            currentCount === 50 ? 100 :
                            currentCount === 100 ? 200 :
                            currentCount === 200 ? 500 : 500,
                            result.matches!.length
                          );
                          setDisplayedMatchCounts(prev => ({
                            ...prev,
                            [resIdx]: nextCount
                          }));
                        }}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Show More Matches ({
                          (() => {
                            const currentCount = displayedMatchCounts[resIdx] || 10;
                            const nextCount = Math.min(
                              currentCount === 10 ? 20 :
                              currentCount === 20 ? 50 :
                              currentCount === 50 ? 100 :
                              currentCount === 100 ? 200 :
                              currentCount === 200 ? 500 : 500,
                              result.matches!.length
                            );
                            return `+${nextCount - currentCount}`;
                          })()
                        })
                      </Button>
                    </Box>
                  )}

                  {/* All matches displayed message */}
                  {result.matches && result.matches.length <= (displayedMatchCounts[resIdx] || 10) && result.matches.length > 0 && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        All {result.matches.length} available matches displayed
                        {result.match_count && result.match_count > result.matches.length && (
                          <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                            ({result.match_count} total found - backend limited to first {result.matches.length})
                          </span>
                        )}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* AI Enhanced Results Section */}
          {enhancedResults && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AIIcon color="primary" />
                AI-Enhanced Analysis
              </Typography>
              
              <Grid container spacing={2}>
                {/* Generated Aliases */}
                {enhancedResults.generated_aliases && enhancedResults.generated_aliases.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Generated Aliases & Variations
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {enhancedResults.generated_aliases.map((alias: string, idx: number) => (
                            <Chip
                              key={idx}
                              label={alias}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {/* Risk Indicators */}
                {enhancedResults.high_risk_indicators && enhancedResults.high_risk_indicators.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom color="warning">
                          Risk Indicators Detected
                        </Typography>
                        {enhancedResults.high_risk_indicators.map((indicator: string, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <WarningIcon color="warning" fontSize="small" />
                            <Typography variant="body2">{indicator}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {/* Semantic Matches */}
                {semanticMatches.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CompareIcon color="primary" />
                          Semantic Similarity Analysis
                        </Typography>
                        <TableContainer sx={{ mt: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Similar Entity</TableCell>
                                <TableCell>List</TableCell>
                                <TableCell>Similarity Score</TableCell>
                                <TableCell>Match Level</TableCell>
                                <TableCell>Recommendation</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {semanticMatches.map((match: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell>{match.name}</TableCell>
                                  <TableCell>
                                    <Chip label={match.list} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    <LinearProgress
                                      variant="determinate"
                                      value={match.semantic.similarity_score * 100}
                                      sx={{ height: 8, borderRadius: 4 }}
                                      color={
                                        match.semantic.similarity_score > 0.8 ? 'error' :
                                        match.semantic.similarity_score > 0.6 ? 'warning' : 'success'
                                      }
                                    />
                                    <Typography variant="caption">
                                      {(match.semantic.similarity_score * 100).toFixed(1)}%
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={match.semantic.match_level}
                                      size="small"
                                      color={
                                        match.semantic.match_level === 'High' ? 'error' :
                                        match.semantic.match_level === 'Medium' ? 'warning' : 'default'
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {match.semantic.recommendation}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {/* Enhanced Summary */}
                {enhancedResults.enhanced_summary && (
                  <Grid item xs={12}>
                    <Alert severity={enhancedResults.enhanced_risk_level === 'High' ? 'error' : 'warning'}>
                      <Typography variant="subtitle2" gutterBottom>
                        Enhanced Risk Assessment
                      </Typography>
                      <Typography variant="body2">
                        {enhancedResults.enhanced_summary}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Summary Alert */}
          {results.some(r => r.is_sanctioned) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Sanctioned Entities Detected
              </Typography>
              <Typography variant="body2">
                One or more entities are on sanctions lists. Transactions with these entities 
                are prohibited and may result in severe penalties. Consult with your compliance 
                officer immediately.
              </Typography>
            </Alert>
          )}

          {!results.some(r => r.is_sanctioned) && !enhancedResults?.high_risk_indicators?.length && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ✅ All Entities Clear
              </Typography>
              <Typography variant="body2">
                No sanctions matches found. However, continue to monitor for updates as 
                sanctions lists change frequently.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SanctionsScreener;